/**
 * 只读白板画布：录像回放与「围观」场景用。
 * 与直播白板共用 lib/wbRender 的绘制代码，保证呈现一致。
 */

import { useEffect, useRef, useState } from 'react'

import { useImageRepaint } from '../lib/useImageRepaint'
import { drawGrid, drawObject } from '../lib/wbRender'
import type { WBPage } from '../lib/whiteboard-types'

interface Props {
  page: WBPage
  /** 额外的类名 */
  className?: string
}

export function WhiteboardView({ page, className }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })
  const repaint = useImageRepaint(page?.objects)

  // 画布跟随容器，保持 4:3。
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const ro = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect()
      const w = rect.width
      const h = Math.min(rect.height, (w * 3) / 4)
      setSize({ w, h })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || size.w === 0) return

    const dpr = window.devicePixelRatio || 1
    canvas.width = size.w * dpr
    canvas.height = size.h * dpr
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.scale(dpr, dpr)

    ctx.fillStyle = '#fbfbf9'
    ctx.fillRect(0, 0, size.w, size.h)
    drawGrid(ctx, size.w, size.h)

    const objs = [...(page?.objects ?? [])].sort((a, b) => a.z - b.z)
    for (const o of objs) drawObject(ctx, o, size.w, size.h)
  }, [page, size, repaint])

  return (
    <div className={`wb-wrap ${className ?? ''}`} ref={wrapRef}>
      <canvas ref={canvasRef} className="wb-canvas" style={{ width: size.w, height: size.h }} />
    </div>
  )
}
